/**
 * The client portal's service selector.
 *
 * A client can hold several services — Support, Invoices, an SMS account or two
 * — and each one is a different job. The selector narrows the portal to one of
 * them so the tab strip shows that job's screens and the shared ones (the
 * overview, the contact book), rather than every screen of every service at once.
 *
 * **Presentation only.** Choosing a service changes what is *shown*. It grants
 * nothing and withholds nothing: the session's `services` are already the
 * client's own, the API re-checks every call, and a pasted link to a screen of
 * another held service still opens. That is why the choice lives in the browser
 * and not on the session.
 *
 * Rendered only when there is a choice to make, as the admin shell's scope
 * switcher is. A client with one service gets no switcher.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  Check,
  ChevronDown,
  Layers,
  LifeBuoy,
  Mail,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Receipt,
  type LucideIcon,
} from 'lucide-react';
import { create } from 'zustand';

import { narrowsTo } from '../../auth/narrows-to';
import { useSession, type ClientService, type NavItem } from '../../auth/session';
import { EMPTY } from '../../lib/empty';
import { cn } from '../../lib/cn';
import { useDismiss } from '../../lib/hooks';
import { loadJSON, saveJSON } from '../../lib/persist';
import { ScrollArea } from '../ui/scroll-area';

export { CORE_SERVICE } from '../../auth/narrows-to';

const STORAGE_SUFFIX = 'portal.active-service';

/**
 * `client public id → chosen entry id`.
 *
 * Keyed by client so a browser used for two clients' portals remembers each one
 * separately, instead of the second sign-in inheriting a choice that names a
 * connection this client does not have.
 */
type Remembered = Record<string, string>;

/**
 * The remembered choices, read on first use rather than at import.
 *
 * `storageKey` is namespaced by the app flavour, which is configured after this
 * module is imported. Reading at module scope would read the unconfigured key
 * and never find what was saved.
 */
interface ChoiceState {
  remembered: Remembered | null;
  choose: (clientId: string, serviceId: string) => void;
}

function loadRemembered(): Remembered {
  return loadJSON<Remembered>(STORAGE_SUFFIX) ?? {};
}

const useChoice = create<ChoiceState>((set, get) => ({
  remembered: null,
  choose: (clientId, serviceId) => {
    const next = { ...(get().remembered ?? loadRemembered()), [clientId]: serviceId };
    saveJSON(STORAGE_SUFFIX, next);
    set({ remembered: next });
  },
}));

/** The chosen entry id for a client, as remembered in this browser. */
export function rememberedServiceId(clientId: string): string | null {
  return (useChoice.getState().remembered ?? loadRemembered())[clientId] ?? null;
}

/** Remember a client's choice in this browser. */
export function rememberServiceId(clientId: string, serviceId: string): void {
  useChoice.getState().choose(clientId, serviceId);
}

/**
 * The active entry: the remembered one while the client still has it, else the
 * first.
 *
 * Falling back rather than trusting the stored id matters: a connection can be
 * paused or removed between two visits, and a selector pointing at an entry that
 * is no longer in the list would show a portal narrowed to nothing.
 */
export function resolveActiveService(
  services: readonly ClientService[],
  chosenId: string | null,
): ClientService | null {
  return services.find((entry) => entry.id === chosenId) ?? services[0] ?? null;
}

/**
 * Whether a navigation item belongs under the active service.
 *
 * Hidden only when it is another selector entry's: a service no entry is — the
 * core, or the account-wide `integrations` (API keys, webhooks, the developer
 * guide) — belongs to the whole account and is shown under every service. The
 * same rule as `Guarded` (`narrowsTo`), so the menu never hides a page the
 * guard lets through. With no active entry — a staff session, or a client the
 * server listed no entries for — everything the server sent is shown, exactly
 * as before the selector existed.
 */
export function isNavItemInService(
  item: Pick<NavItem, 'service' | 'feature'>,
  active: ClientService | null,
  services: readonly ClientService[],
): boolean {
  if (!active) return !item.feature;
  if (item.feature && !(active.features ?? []).includes(item.feature)) return false;
  return item.service === active.service || !narrowsTo(services, item.service);
}

/**
 * What the active service calls a module: its own word where it has one, the
 * catalog's otherwise. One rule for the menu and for the page's own title, so
 * the two never name the same page differently.
 */
export function moduleLabel(id: string, catalogLabel: string, active: ClientService | null): string {
  return active?.labels?.[id] ?? catalogLabel;
}

/**
 * A module's label under the active service, read from the manifest — a
 * module or one of a module's pages. `fallback` only for a module the
 * session's manifest does not list, which no page it guards can be.
 */
export function useModuleLabel(id: string, fallback: string): string {
  const navigation = useSession((state) => state.user?.navigation ?? EMPTY);
  const { active } = useActiveClientService();
  for (const group of navigation) {
    for (const item of group.items) {
      if (item.id === id) return moduleLabel(id, item.label, active);
      const page = item.pages?.find((entry) => entry.id === id);
      if (page) return moduleLabel(id, page.label, active);
    }
  }
  return moduleLabel(id, fallback, active);
}

export interface ActiveClientService {
  /** Every entry the client has, in the server's order. */
  services: readonly ClientService[];
  /** The one the portal is narrowed to, or `null` when there are none. */
  active: ClientService | null;
  choose: (serviceId: string) => void;
}

/** The client's entries, the active one, and a way to change it. */
export function useActiveClientService(): ActiveClientService {
  const services = useSession((state) => state.user?.client_services ?? EMPTY);
  const clientId = useSession((state) => state.user?.client?.public_id ?? null);
  // A string or null, never a fresh object: Zustand compares snapshots by
  // identity, and a selector building an object each call never settles.
  const chosenId = useChoice((state) =>
    clientId ? ((state.remembered ?? loadRemembered())[clientId] ?? null) : null,
  );
  const choose = useChoice((state) => state.choose);

  return {
    services,
    active: resolveActiveService(services, chosenId),
    choose: (serviceId) => {
      if (clientId) choose(clientId, serviceId);
    },
  };
}

export interface ServiceSwitcherProps {
  services: readonly ClientService[];
  active: ClientService | null;
  onChange: (entry: ClientService) => void;
}

/** A messaging channel as a client calls it. Unknown channels are shown upper-cased. */
const CHANNEL_NAMES: Record<string, string> = {
  sms: 'SMS',
  email: 'Email',
  whatsapp: 'WhatsApp',
  rcs: 'RCS',
};

const CHANNEL_ICONS: Record<string, LucideIcon> = {
  sms: MessageSquare,
  email: Mail,
  whatsapp: MessageCircle,
  rcs: MessagesSquare,
};

/** The icon for an entry: by channel for a messaging account, by service otherwise. */
export function serviceIcon(entry: Pick<ClientService, 'service' | 'channel'>): LucideIcon {
  if (entry.service === 'messaging') return (entry.channel && CHANNEL_ICONS[entry.channel]) || MessageSquare;
  if (entry.service === 'support') return LifeBuoy;
  if (entry.service === 'finance') return Receipt;
  return Layers;
}

/**
 * The one line under an entry's name, saying what working in it means.
 *
 * The name alone is the provider's label for a messaging account ("Bulk SMS"),
 * which does not say it is a messaging account at all.
 */
export function serviceDescription(entry: Pick<ClientService, 'service' | 'channel'>): string {
  if (entry.service === 'messaging') {
    const channel = entry.channel ? (CHANNEL_NAMES[entry.channel] ?? entry.channel.toUpperCase()) : null;
    return channel ? `${channel} messaging account` : 'Messaging account';
  }
  if (entry.service === 'support') return 'Support tickets';
  if (entry.service === 'finance') return 'Invoices and payments';
  return 'Service';
}

/**
 * The switcher: a trigger naming the active service, and a panel of every one.
 *
 * A `menu` of `menuitemradio`s — one of them is checked, and choosing another
 * is an action, not a form value. Focus goes to the checked entry on open,
 * moves with the arrow keys, Home and End, and returns to the trigger when a
 * choice or Escape closes the panel.
 *
 * The panel renders in place under the trigger rather than through `Popover`:
 * `Popover` mounts its panel a render later and dismisses on a pointer-down
 * outside the panel, which is the trigger — so focusing the checked entry on
 * open would race the mount, and the trigger could not close what it opened.
 * The dismiss area here is the trigger and the panel together.
 *
 * Renders nothing unless there are at least two services.
 */
export function ServiceSwitcher({ services, active, onChange }: ServiceSwitcherProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const activeIndex = Math.max(
    0,
    services.findIndex((entry) => entry.id === active?.id),
  );

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  // Escape is `useDismiss`'s too, and must give focus back; an outside click
  // must not, or it would steal focus from whatever was clicked.
  useDismiss(rootRef, () => close(false), open);

  useEffect(() => {
    if (open) itemRefs.current[activeIndex]?.focus();
    // Only on opening: re-focusing on every render would fight the arrow keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (services.length < 2) return null;

  const focusItem = (index: number) => {
    const count = services.length;
    itemRefs.current[(index + count) % count]?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = itemRefs.current.findIndex((item) => item === document.activeElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(current + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItem(current - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(services.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        // Handled here, before the document listener, so focus goes back.
        event.nativeEvent.stopImmediatePropagation();
        close(true);
        break;
      case 'Tab':
        // Leaving the menu closes it; focus carries on to wherever Tab goes.
        setOpen(false);
        break;
      default:
    }
  };

  const ActiveIcon = active ? serviceIcon(active) : Layers;

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        // Says what changing it does, so it does not read as a filter over the
        // same screens.
        title="Choose which of your services to work in. The tabs change to match."
        className={cn(
          'flex h-8 max-w-full items-center gap-1.5 rounded-control border px-2.5 text-xs transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          open
            ? 'border-accent/40 bg-accent-subtle text-accent'
            : 'border-line text-body hover:border-line-strong hover:bg-surface-2',
        )}
      >
        <ActiveIcon className="size-3.5 shrink-0" aria-hidden />
        {/* Narrower on a phone, where the top bar also carries the brand, the
            theme toggle, the avatar and the menu button. */}
        <span className="min-w-0 max-w-24 truncate text-left font-medium sm:max-w-44">
          {active?.label ?? 'Service'}
        </span>
        <ChevronDown
          className={cn('size-3.5 shrink-0 text-faint transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Your services"
          onKeyDown={onMenuKeyDown}
          className={cn(
            'z-dropdown animate-scale-in overflow-hidden rounded-card border border-line bg-surface-popup shadow-dropdown',
            // On a phone the trigger sits mid-bar, so a card aligned to it would
            // run off one edge: it spans the screen under the bar instead. Wider,
            // a card aligned to the trigger's right edge.
            'fixed inset-x-4 top-14 sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-1.5 sm:w-80',
          )}
        >
          <div className="border-b border-line px-3.5 py-2.5">
            <p className="text-xs font-semibold text-heading">Your services</p>
            <p className="text-2xs text-faint">The tabs change to match the one you work in.</p>
          </div>
          <ScrollArea className="max-h-[min(22rem,60dvh)]" contentClassName="p-1.5">
            {services.map((entry, index) => {
              const checked = entry.id === active?.id;
              const Icon = serviceIcon(entry);
              return (
                <button
                  key={entry.id}
                  ref={(node) => {
                    itemRefs.current[index] = node;
                  }}
                  type="button"
                  role="menuitemradio"
                  aria-checked={checked}
                  // Roving focus: only the arrows move between entries, so Tab
                  // leaves the menu in one press.
                  tabIndex={index === activeIndex ? 0 : -1}
                  onClick={() => {
                    close(true);
                    if (!checked) onChange(entry);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-control px-2.5 py-2 text-left transition-colors',
                    'focus:outline-none focus-visible:bg-surface-2 hover:bg-surface-2',
                    checked && 'bg-accent-subtle hover:bg-accent-subtle focus-visible:bg-accent-subtle',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-control',
                      checked ? 'bg-accent text-white' : 'bg-surface-2 text-muted',
                    )}
                  >
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-sm',
                        checked ? 'font-semibold text-accent' : 'font-medium text-body',
                      )}
                    >
                      {entry.label}
                    </span>
                    <span className="block truncate text-2xs text-faint">{serviceDescription(entry)}</span>
                  </span>
                  <Check
                    className={cn('size-4 shrink-0 text-accent', checked ? 'opacity-100' : 'opacity-0')}
                    aria-hidden
                  />
                </button>
              );
            })}
          </ScrollArea>
        </div>
      ) : null}
    </div>
  );
}
